import { useCallback, useEffect } from "react";
import { AccountingUser, CreateAccountingUser, UpdateAccountingUser } from "../types/models/AccountingUser.d";
import { UpdateEntityAccountingUser } from "../types/models/AccountingUser";
import { useGetAccountingUserListQuery, useInsertAccountingUserMutation, useUpdateAccountingUserMutation, useDeleteAccountingUserMutation, } from "../apis/AccountingUser.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useAccountingUser(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertAccountingUserMutation, { 
        isLoading: insertAccountingUserLoading, 
        isSuccess: insertAccountingUserIsSuccess,
        isError: insertAccountingUserIsError,
        error: insertAccountingUserError,
    }] = useInsertAccountingUserMutation()
    const [updateAccountingUserMutation, { 
        isLoading: updateAccountingUserLoading,
        isSuccess: updateAccountingUserIsSuccess,
        isError: updateAccountingUserIsError,
        error: updateAccountingUserError,
    }] = useUpdateAccountingUserMutation()
    const [deleteAccountingUserMutation, { 
        isLoading: deleteAccountingUserLoading,
        isSuccess: deleteAccountingUserIsSuccess,
        isError: deleteAccountingUserIsError,
        error: deleteAccountingUserError,
    }] = useDeleteAccountingUserMutation()

    const loading =
        insertAccountingUserLoading ||
        updateAccountingUserLoading ||
        deleteAccountingUserLoading ||
        false

    const insertAccountingUser = useCallback(async (accountingUser: CreateAccountingUser, redirectPath: string|undefined) => {
        try {
            const data = await insertAccountingUserMutation(accountingUser).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertAccountingUserMutation, navigate])
    
    useEffect(() => {
        if (insertAccountingUserIsSuccess) {
            openSuccessAlert('Accounting User created')
        } else if (insertAccountingUserIsError) {
            console.log("add error", insertAccountingUserError)
            openFailAlert(t`Accounting User add error: ` + insertAccountingUserError?.data?.message)
        }
    }, [
        insertAccountingUserIsSuccess,
        insertAccountingUserIsError,
        insertAccountingUserError
    ]);

    const updateAccountingUser = useCallback(async (accountingUser: UpdateEntityAccountingUser, redirectPath: string|undefined) => {
        try {
            const data = await updateAccountingUserMutation(accountingUser).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateAccountingUserMutation, navigate])
    
    useEffect(() => {
        if (updateAccountingUserIsSuccess) {
            openSuccessAlert(t`Accounting User updated`)
        } else if (updateAccountingUserIsError) {
            console.log("update error", updateAccountingUserError)
            openFailAlert(t`Accounting User update error: ` + updateAccountingUserError?.data?.message)
        }
    }, [
        updateAccountingUserIsSuccess,
        updateAccountingUserIsError,
        updateAccountingUserError
    ]);

    const deleteAccountingUser = useCallback((accountingUser: AccountingUser, redirectPath: string|undefined) => {
        openDialog(async () => {
            accountingUser = {
                ...accountingUser,
            }
            try {
                const { data } = await deleteAccountingUserMutation(accountingUser);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteAccountingUserMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteAccountingUserIsSuccess) {
            openSuccessAlert(t`Accounting User deleted`)
        } else if (deleteAccountingUserIsError) {
            console.log("delete error", deleteAccountingUserError)
            openFailAlert(t`Accounting User delete error: ` + deleteAccountingUserError?.data?.message)
        }
    }, [
        deleteAccountingUserIsSuccess,
        deleteAccountingUserIsError,
        deleteAccountingUserError
    ]);

    return {
        loading,
        insertAccountingUser,
        updateAccountingUser,
        deleteAccountingUser
    }
}
